/** Bandes libres de la carte entre les panneaux flottants (`[data-floating-panel]` : vue, calques, fiche,
 *  assistant) : où ranger les bandeaux du haut de la carte (tracé, édition du fuseau — docs/14 §2.3 —, rappel de
 *  l'analyse du terrain), les barres basses et l'avis sans qu'ils passent jamais sur un panneau ni sur l'échelle, le
 *  logo ou l'attribution du fond de carte (R7). Fonctions pures sur des rectangles (pixels de la page) : MapCanvas les mesure, les tests les
 *  rejouent. */

export interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

export interface Band {
  /** Milieu de la bande, en pixels depuis le bord gauche de la carte. */
  center: number;
  /** Largeur utile (12 px de marge de chaque côté déduits). */
  width: number;
}

/** Disposition du bandeau : complète (consigne et boutons en une rangée), compacte (consigne derrière « ? »,
 *  « Retirer » en icône) ou minimale (icônes seules, sur autant de rangées qu'il faut). */
export type BannerLayout = "full" | "compact" | "mini";

export interface BannerPlace extends Band {
  /** Haut du bandeau, en pixels depuis le bord haut de la carte. */
  top: number;
  layout: BannerLayout;
}

/** En deçà de cette largeur libre (px), le bandeau passe en disposition compacte : sa rangée complète (↶ ↷,
 *  « Garder la largeur », « Retirer le sommet », « Terminé ») déborderait de la bande. */
export const BANNER_FULL_MIN_PX = 540;
/** En deçà, même la disposition compacte (titre, « ? », ↶ ↷, « Garder la largeur », « Terminé ») ne tient plus :
 *  disposition minimale. */
export const BANNER_COMPACT_MIN_PX = 340;
/** Hauteur réservée au bandeau compact (deux rangées) quand on cherche une tranche libre plus bas. */
const BANNER_SLICE_PX = 96;
/** Pas de la recherche d'une tranche libre plus bas. */
const SCAN_STEP_PX = 40;
/** Bas de la carte laissé aux avis et à la bande basse (temps, relevé, zoom). */
const BOTTOM_RESERVED_PX = 150;
/** Haut d'une barre courte collée en haut (calques repliés) : elle commence dans les 80 premiers pixels. */
const TOP_STRIP_PX = 80;

const visible = (box: Rect, panels: readonly Rect[]) =>
  panels.filter((r) => r.width > 0 && r.height > 0 && r.right > box.left && r.left < box.right);

/** Bande horizontale libre entre les panneaux qui occupent la tranche verticale [`top`, `bottom`] de la carte
 *  (pixels depuis son bord haut) : entre le bord droit de ceux de gauche et le bord gauche de ceux de droite,
 *  12 px de marge de chaque côté. null si la carte n'est pas mesurable. */
export function bandBetween(box: Rect, panels: readonly Rect[], top: number, bottom: number): Band | null {
  if (box.width <= 0) return null;
  let left = box.left;
  let right = box.right;
  for (const r of visible(box, panels)) {
    if (r.bottom <= box.top + top || r.top >= box.top + bottom) continue;
    if ((r.left + r.right) / 2 < box.left + box.width / 2) left = Math.max(left, r.right);
    else right = Math.min(right, r.left);
  }
  return { center: (left + right) / 2 - box.left, width: Math.max(0, right - left - 24) };
}

const layoutFor = (width: number): BannerLayout =>
  width >= BANNER_FULL_MIN_PX ? "full" : width >= BANNER_COMPACT_MIN_PX ? "compact" : "mini";

/** Place du bandeau, jamais sur un panneau :
 *  1. la bande libre du haut (80 premiers pixels), disposition complète ou compacte selon sa largeur ;
 *  2. sinon, sous les barres courtes collées en haut (calques repliés à côté du panneau de la vue), entre les
 *     panneaux hauts ;
 *  3. sinon, la première tranche plus bas assez large pour la disposition compacte (sous une fiche courte, par
 *     exemple), au-dessus des avis et de la bande basse ;
 *  4. sinon (panneaux hauts des deux côtés, carte étroite), la disposition minimale dans la plus large des deux
 *     premières bandes : icônes seules, sur autant de rangées qu'il faut, bornées à la bande. */
export function bannerPlace(box: Rect, panels: readonly Rect[]): BannerPlace | null {
  const top = bandBetween(box, panels, 0, TOP_STRIP_PX);
  if (!top) return null;
  if (top.width >= BANNER_COMPACT_MIN_PX) return { ...top, top: 12, layout: layoutFor(top.width) };
  const shown = visible(box, panels);
  let below = 0;
  for (const r of shown)
    if (r.top - box.top < TOP_STRIP_PX && r.height < box.height * 0.25) below = Math.max(below, r.bottom - box.top);
  const candidates: (Band & { top: number })[] = [{ ...top, top: 12 }];
  if (below) {
    const lower = bandBetween(box, panels, below + 12, below + 12 + BANNER_SLICE_PX - 16);
    if (lower) {
      if (lower.width >= BANNER_COMPACT_MIN_PX) return { ...lower, top: below + 12, layout: layoutFor(lower.width) };
      candidates.push({ ...lower, top: below + 12 });
    }
  }
  const last = box.height - BOTTOM_RESERVED_PX - BANNER_SLICE_PX;
  for (let y = Math.max(12, below + 12) + SCAN_STEP_PX; y <= last; y += SCAN_STEP_PX) {
    const slice = bandBetween(box, panels, y, y + BANNER_SLICE_PX);
    if (slice && slice.width >= BANNER_COMPACT_MIN_PX) return { ...slice, top: y, layout: layoutFor(slice.width) };
  }
  const widest = candidates.reduce((a, b) => (b.width > a.width ? b : a));
  return { ...widest, layout: "mini" };
}

/** Bande libre en bas de la carte (temps de la manœuvre, relevé MGRS, zoom) : les barres basses, hautes de
 *  `height` px à 12 px du bord, se rangent dans la bande libre (null sinon : centrées sur la carte) quand, centrées,
 *  elles croiseraient :
 *  - un panneau (fenêtre étroite où elles passent sur deux lignes : les panneaux s'arrêtent à 64 px du bas) ;
 *  - un élément du fond de carte (`obstacles` : échelle, logo et attribution des coins bas, R7), qu'elles ne
 *    recouvrent jamais. `barWidth` : largeur des barres centrées (inconnue : tout obstacle de la tranche compte). */
export function bottomBandFor(
  box: Rect,
  panels: readonly Rect[],
  height: number,
  obstacles: readonly Rect[] = [],
  barWidth?: number,
): Band | null {
  if (box.height <= 0 || height <= 0) return null;
  const top = box.height - 12 - height - 4;
  const inSlice = (r: Rect) => r.bottom > box.top + top && r.top < box.bottom;
  const crossedPanel = visible(box, panels).some(inSlice);
  const middle = box.left + box.width / 2;
  const crossedObstacle = visible(box, obstacles).some(
    (r) => inSlice(r) && (barWidth === undefined || (r.left < middle + barWidth / 2 && r.right > middle - barWidth / 2)),
  );
  return crossedPanel || crossedObstacle ? bandBetween(box, [...panels, ...obstacles], top, box.height) : null;
}

/** Hauteur réservée à un avis (titre et détail sur deux lignes) dans la recherche de sa bande. */
export const NOTICE_SLICE_PX = 84;
/** En deçà de cette largeur libre (px), l'avis ne tient plus entre les panneaux : centré sur la carte, au-dessus. */
export const NOTICE_MIN_PX = 280;

/** Place de l'avis (`notify`, R10) : juste au-dessus des barres basses (`barsHeight` px à 12 px du bord, plus 12 px
 *  d'écart), centré dans la bande libre que laissent les panneaux sur sa hauteur — il ne chevauche ni la barre
 *  d'état ni un panneau. `bottom` : distance au bord bas de la carte ; `band` null : centré sur la carte (bande trop
 *  étroite, carte non mesurable). `avoided` : bulles posées au-dessus des vues (prise en main de l'éditeur d'ordre de
 *  bataille) que l'avis ne recouvre jamais : à côté d'elles s'il y tient, sinon au-dessus, 12 px plus haut. */
export function noticePlace(
  box: Rect,
  panels: readonly Rect[],
  barsHeight: number,
  obstacles: readonly Rect[] = [],
  avoided: readonly Rect[] = [],
): { bottom: number; band: Band | null } {
  const bottom = 12 + Math.max(0, barsHeight) + 12;
  if (box.width <= 0 || box.height <= 0) return { bottom, band: null };
  const fit = (from: number) => {
    const band = bandBetween(box, [...panels, ...obstacles, ...avoided], box.height - from - NOTICE_SLICE_PX, box.height - from);
    return band && band.width >= NOTICE_MIN_PX ? band : null;
  };
  const band = fit(bottom);
  const crossed = visible(box, avoided).filter(
    (r) => r.bottom > box.top + box.height - bottom - NOTICE_SLICE_PX && r.top < box.top + box.height - bottom,
  );
  if (band || !crossed.length) return { bottom, band };
  // Trop peu de place à côté de la bulle : l'avis passe au-dessus d'elle.
  const above = Math.round(box.bottom - Math.min(...crossed.map((r) => r.top)) + 12);
  return { bottom: Math.max(bottom, above), band: fit(Math.max(bottom, above)) };
}

/** Éléments facultatifs de la barre d'état, du plus au moins utile : relevé MGRS (puis le même resserré à la
 *  largeur du texte) et niveau de zoom. Les boutons (↶ ↷ hors du mode édition, zoom, cadrage) restent toujours.
 *  Dans une bande libre étroite (barres rangées à côté des panneaux), les éléments cèdent la place dans l'ordre
 *  inverse : le zoom, puis la réserve du relevé, et le relevé en dernier. Largeurs mesurées sur la barre affichée
 *  (repli : valeurs de la feuille de style). */
export type StatusFit = { mgrs: "full" | "tight" | "hidden"; zoom: boolean; osm?: "full" | "short" };
export const STATUS_ALL: StatusFit = { mgrs: "full", zoom: true };
/** Réserve du relevé (largeur fixe : il ne tressaute pas quand la coordonnée change), puis resserré au texte. */
export const MGRS_FULL_PX = 178;
export const MGRS_TIGHT_PX = 146;
export const STATUS_GAP_PX = 4;

/** Ce qui tient dans `room` pixels : `core` = barre sans éléments facultatifs ; `widths` = largeur de chacun (écart
 *  compris), relevé à sa réserve complète (0 : relevé absent, curseur hors de la carte ou écran tactile).
 *  `widths.osm` (zone libre) : largeur ajoutée par la mention OpenStreetMap complète par rapport à sa forme courte
 *  « © OSM » (comptée dans `core`) ; la mention se raccourcit avant que le zoom et le relevé cèdent la place (le
 *  texte complet reste dans l'info-bulle) ; `Infinity` : forme courte imposée (écran étroit, doigt). */
export function statusFit(room: number, core: number, widths: { mgrs: number; zoom: number; osm?: number }): StatusFit {
  const withOsm = (fit: StatusFit, used: number): StatusFit =>
    widths.osm === undefined
      ? fit
      : { ...fit, osm: Number.isFinite(widths.osm) && (!Number.isFinite(room) || used + widths.osm <= room) ? "full" : "short" };
  if (!Number.isFinite(room)) return withOsm(STATUS_ALL, core);
  let used = core;
  let mgrs: StatusFit["mgrs"] = "hidden";
  if (used + widths.mgrs <= room) mgrs = "full";
  else if (used + widths.mgrs - (MGRS_FULL_PX - MGRS_TIGHT_PX) <= room) mgrs = "tight";
  if (mgrs !== "hidden") used += widths.mgrs - (mgrs === "tight" ? MGRS_FULL_PX - MGRS_TIGHT_PX : 0);
  const zoom = mgrs === "full" && used + widths.zoom <= room;
  if (zoom) used += widths.zoom;
  return withOsm({ mgrs, zoom }, used);
}

/** Rang d'une disposition de la barre d'état : plus il est bas, moins elle prend de place. */
function statusRank(fit: StatusFit): number {
  return (fit.mgrs === "full" ? 4 : fit.mgrs === "tight" ? 2 : 0) + (fit.zoom ? 1 : 0);
}

const sameStatus = (a: StatusFit, b: StatusFit) => a.mgrs === b.mgrs && a.zoom === b.zoom && a.osm === b.osm;

/** Garde contre le va-et-vient de la barre d'état (incident du 20/10/2026 : « Maximum update depth exceeded » en
 *  fermant une fiche, la barre passant sans fin d'une disposition à l'autre, chaque disposition changeant la
 *  largeur mesurée qui décidait de la suivante) : `recent` garde les dernières dispositions retenues depuis la
 *  dernière variation de la place disponible ; une disposition qui ramènerait l'une d'elles (autre que
 *  l'actuelle) est un aller-retour — la plus compacte des deux est gardée, et la barre ne bouge plus. */
export function settleStatusFit(current: StatusFit, next: StatusFit, recent: readonly StatusFit[]): StatusFit {
  if (sameStatus(current, next)) return current;
  const back = recent.some((fit) => !sameStatus(fit, current) && sameStatus(fit, next));
  if (!back) return next;
  return statusRank(next) <= statusRank(current) ? next : current;
}

/** Bandeau du haut de la carte affiché (un seul à la fois, R10) : tracé d'un graphique ou d'une zone, édition du
 *  fuseau, rappel de l'analyse du terrain — dans cet ordre de priorité. null : aucun. */
export type TopBanner = "zone" | "draft" | "edit" | "orient" | "terrain";
/** Bandeau du haut de la carte, un seul à la fois : tracé de la zone libre (axe, extension) > tracé d'un graphique
 *  > édition du fuseau > rappel de l'analyse du terrain. */
export function topBannerFor(state: {
  zone?: boolean;
  draft: boolean;
  editing: boolean;
  /** Mode « Orienter la manœuvre » (docs/14 §2.14) : même rang que l'édition du fuseau (ils s'excluent). */
  orienting?: boolean;
  terrainRecall: boolean;
}): TopBanner | null {
  if (state.zone) return "zone";
  if (state.draft) return "draft";
  if (state.editing) return "edit";
  if (state.orienting) return "orient";
  return state.terrainRecall ? "terrain" : null;
}

/** Le bloc titre de la barre des temps tient-il dans la bande basse (`room` px) ? En deçà d'environ 520 px, il
 *  serait tronqué (« 11B… ») : il se masque, le titre passant dans la bulle du symbole. */
export const PHASE_TITLE_MIN_PX = 520;
export const phaseTitleFits = (room: number) => !(room < PHASE_TITLE_MIN_PX);
